import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Package,
  Plus,
  Copy,
  Check,
  CheckCircle2,
  Clock,
  Trash2,
  ExternalLink,
  Image,
  Link as LinkIcon,
  DollarSign,
  AlertCircle,
  ArrowLeft,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  unpaid: { label: "Awaiting Payment", color: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/20" },
  pending_wire: { label: "Wire Pending", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/20" },
  paid: { label: "Paid", color: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/20" },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || statusConfig.unpaid;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
      {status === "paid" ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 h-7 text-xs">
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

// ── Create Delivery Dialog ────────────────────────────────────────────────────

function CreateDeliveryDialog({ programId, programName, onCreated, open, onOpenChange }) {
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    amount: "",
    dropbox_link: "",
    notes: "",
    imageUrls: [""],
  });

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const setImageUrl = (idx, val) => {
    const next = [...form.imageUrls];
    next[idx] = val;
    setForm((f) => ({ ...f, imageUrls: next }));
  };

  const addImageRow = () => setForm((f) => ({ ...f, imageUrls: [...f.imageUrls, ""] }));
  const removeImageRow = (idx) =>
    setForm((f) => ({ ...f, imageUrls: f.imageUrls.filter((_, i) => i !== idx) }));

  const reset = () =>
    setForm({ title: "", amount: "", dropbox_link: "", notes: "", imageUrls: [""] });

  const handleSubmit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      return toast.error("Enter a valid amount");

    const images = form.imageUrls
      .filter((u) => u.trim())
      .map((u) => ({ url: u.trim(), filename: u.trim().split("/").pop().split("?")[0], caption: "" }));

    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/deliveries`,
        {
          program_id: programId,
          program_name: programName,
          title: form.title.trim(),
          images,
          dropbox_link: form.dropbox_link.trim(),
          amount: Number(form.amount),
          notes: form.notes.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Delivery package created");
      reset();
      onOpenChange(false);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create delivery");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-4 h-4" />
            New Delivery Package
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title *</Label>
            <Input
              placeholder="e.g. Final Renders — Phase 1"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Payment Amount (USD) *</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5">
              <LinkIcon className="w-3.5 h-3.5" />
              Dropbox Link
            </Label>
            <Input
              placeholder="https://www.dropbox.com/sh/..."
              value={form.dropbox_link}
              onChange={(e) => set("dropbox_link", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This link is hidden from the client until payment is confirmed.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1.5">
              <Image className="w-3.5 h-3.5" />
              Preview Images
            </Label>
            <p className="text-xs text-muted-foreground">
              Add image URLs. Clients see a blurred preview before paying.
            </p>
            {form.imageUrls.map((url, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => setImageUrl(idx, e.target.value)}
                />
                {form.imageUrls.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-9 w-9 text-muted-foreground hover:text-destructive"
                    onClick={() => removeImageRow(idx)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addImageRow} className="gap-1.5 text-xs">
              <Plus className="w-3 h-3" />
              Add image
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Input
              placeholder="Any message to include for the client"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Creating…" : "Create Package"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Wire Confirm Dialog ────────────────────────────────────────────────────────

function WireConfirmDialog({ delivery, open, onOpenChange, onConfirmed }) {
  const { token } = useAuth();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/deliveries/${delivery.id}/confirm-wire`,
        { note: note.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Wire payment confirmed — assets are now unlocked for the client");
      onOpenChange(false);
      onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to confirm payment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            Confirm Wire Payment
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Confirming wire receipt will unlock all delivery assets for the client immediately.
          </p>
          <div className="space-y-1.5">
            <Label>Internal note (optional)</Label>
            <Input
              placeholder="e.g. Received via Chase on June 23"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={saving} className="bg-green-600 hover:bg-green-700">
            {saving ? "Confirming…" : "Confirm Payment Received"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delete Confirm Dialog ─────────────────────────────────────────────────────

function DeleteConfirmDialog({ delivery, open, onOpenChange, onDeleted }) {
  const { token } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/deliveries/${delivery.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Delivery package deleted");
      onOpenChange(false);
      onDeleted(delivery.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="w-4 h-4" />
            Delete Delivery Package
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-2">
          Delete <span className="font-medium text-foreground">{delivery?.title}</span>? This cannot be undone.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Delivery Card ─────────────────────────────────────────────────────────────

function DeliveryCard({ delivery, onRefresh }) {
  const [wireOpen, setWireOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const clientUrl = `${window.location.origin}/delivery/${delivery.client_token}`;
  const isPaid = delivery.payment_status === "paid";

  return (
    <>
      <Card className="border border-border">
        <CardContent className="p-4 space-y-3">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{delivery.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Created {new Date(delivery.created_at).toLocaleDateString()} · {delivery.image_count ?? delivery.images?.length ?? 0} image(s)
              </p>
            </div>
            <StatusBadge status={delivery.payment_status} />
          </div>

          {/* Amount */}
          <div className="flex items-center gap-1.5 text-sm">
            <DollarSign className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-medium">${delivery.amount?.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
          </div>

          {/* Dropbox link (PM can always see it) */}
          {delivery.dropbox_link && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <LinkIcon className="w-3 h-3 shrink-0" />
              <a
                href={delivery.dropbox_link}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate hover:text-foreground hover:underline"
              >
                {delivery.dropbox_link}
              </a>
            </div>
          )}

          {/* Payment info when paid */}
          {isPaid && (
            <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Paid via {delivery.payment_method === "wire" ? "wire transfer" : "Stripe"}
              {delivery.wire_confirmed_by_name && ` · confirmed by ${delivery.wire_confirmed_by_name}`}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <CopyButton text={clientUrl} />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => window.open(clientUrl, "_blank")}
            >
              <ExternalLink className="w-3 h-3" />
              Preview
            </Button>

            {!isPaid && delivery.payment_method === "wire" && (
              <Button
                size="sm"
                className="h-7 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => setWireOpen(true)}
              >
                <CheckCircle2 className="w-3 h-3" />
                Confirm wire received
              </Button>
            )}

            {!isPaid && !delivery.payment_method && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
                <AlertCircle className="w-3 h-3" />
                Waiting for client payment
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive ml-auto"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <WireConfirmDialog
        delivery={delivery}
        open={wireOpen}
        onOpenChange={setWireOpen}
        onConfirmed={onRefresh}
      />
      <DeleteConfirmDialog
        delivery={delivery}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onRefresh}
      />
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

/**
 * Renders standalone at /programs/:programId/delivery, and inline as the
 * project page's Delivery tab. When `embedded`, it drops its own page chrome
 * (back button, title, outer padding) so it sits in the tab body like every
 * other tab.
 */
export default function DeliveryManager({ programId: programIdProp, embedded = false }) {
  const params = useParams();
  const programId = programIdProp || params.programId;
  const navigate = useNavigate();
  const { token } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [programName, setProgramName] = useState("");

  const fetchDeliveries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deliveries?program_id=${programId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliveries(res.data);
    } catch (e) {
      toast.error("Failed to load deliveries");
    } finally {
      setLoading(false);
    }
  }, [programId, token]);

  // Fetch program name — only for the standalone page's subtitle.
  useEffect(() => {
    if (!programId || embedded) return;
    axios
      .get(`${API}/programs/${programId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setProgramName(r.data.program_name || ""))
      .catch(() => {});
  }, [programId, token, embedded]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handleCreated = (newDelivery) => {
    setDeliveries((prev) => [newDelivery, ...prev]);
  };

  const handleRefresh = () => fetchDeliveries();

  return (
    <div className={embedded ? "space-y-4" : "max-w-3xl mx-auto p-4 md:p-6 space-y-6"}>
      {/* Header — the standalone page needs its own nav; the tab does not. */}
      {embedded ? (
        <div className="flex items-center justify-between gap-3">
          <span className="label-text">Delivery Packages</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="h-8 rounded-full px-3 text-[13px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            New Package
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate(`/programs/${programId}`)}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-4 h-4" />
              Delivery Packages
            </h1>
            {programName && (
              <p className="text-xs text-muted-foreground truncate">{programName}</p>
            )}
          </div>
          <Button size="sm" className="ml-auto gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="w-3.5 h-3.5" />
            New Package
          </Button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
      ) : deliveries.length === 0 ? (
        <Card className="border border-dashed border-border">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <Package className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No delivery packages yet</p>
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Create first package
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {deliveries.map((d) => (
            <DeliveryCard key={d.id} delivery={d} onRefresh={handleRefresh} />
          ))}
        </div>
      )}

      <CreateDeliveryDialog
        programId={programId}
        programName={programName}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={handleCreated}
      />
    </div>
  );
}
